import { Component, Inject, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatDialogModule, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatDividerModule } from '@angular/material/divider';
import { PaymentService } from '../../../../core/services/payment.service';
import { NotificationService } from '../../../../core/services/notification.service';
import { SubscriptionService } from '../../../../core/services/subscription.service';
import { PaymentResponse, PaymentStatusType } from '../../../../core/models/payment.model';
import { RadicalEvent } from '../../../../core/models/event.model';
import { Subscription, interval } from 'rxjs';
import { switchMap, takeWhile } from 'rxjs/operators';

@Component({
  selector: 'app-pix-payment-dialog',
  standalone: true,
  imports: [
    CommonModule,
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatDividerModule,
  ],
  templateUrl: './pix-payment-dialog.component.html',
  styleUrls: ['./pix-payment-dialog.component.scss']
})
export class PixPaymentDialogComponent implements OnInit, OnDestroy {
  payment: PaymentResponse | null = null;
  loading = true;
  error: string | null = null;
  countdown = '';
  paymentConfirmed = false;

  private pollingSubscription: Subscription | null = null;
  private countdownInterval: ReturnType<typeof setInterval> | null = null;

  constructor(
    private paymentService: PaymentService,
    private notification: NotificationService,
    private subscriptionService: SubscriptionService,
    public dialogRef: MatDialogRef<PixPaymentDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: { registrationId: number; event: RadicalEvent }
  ) {}

  ngOnInit(): void {
    this.createPayment();
  }

  ngOnDestroy(): void {
    this.stopPolling();
    this.stopCountdown();
  }

  private createPayment(): void {
    this.loading = true;
    this.error = null;

    this.paymentService.createPayment(this.data.registrationId).subscribe({
      next: (payment) => {
        this.payment = payment;
        this.loading = false;
        this.startCountdown();
        this.startPolling();
      },
      error: (err) => {
        this.loading = false;
        this.error = err.error?.message || 'Erro ao gerar cobrança PIX. Tente novamente.';
      }
    });
  }

  copyPixCode(): void {
    if (this.payment?.pixCopyPaste) {
      navigator.clipboard.writeText(this.payment.pixCopyPaste).then(() => {
        this.notification.success('Codigo PIX copiado!');
      });
    }
  }

  private startPolling(): void {
    if (!this.payment) return;

    this.pollingSubscription = interval(5000).pipe(
      switchMap(() => this.paymentService.getPaymentStatus(this.payment!.id)),
      takeWhile((status) => {
        const confirmed = status.status === 'CONFIRMED' || status.status === 'RECEIVED';
        const failed = status.status === 'FAILED' || status.status === 'EXPIRED';
        return !confirmed && !failed;
      }, true)
    ).subscribe({
      next: (status) => {
        if (status.status === 'CONFIRMED' || status.status === 'RECEIVED') {
          this.paymentConfirmed = true;
          this.stopCountdown();
          this.notification.success('Pagamento confirmado! Sua vaga esta garantida.');
          this.subscriptionService.loadMyRegistrations();
          setTimeout(() => this.dialogRef.close(true), 3000);
        } else if (status.status === 'FAILED' || status.status === 'EXPIRED') {
          this.error = 'Pagamento expirado ou falhou. Tente novamente.';
          this.stopCountdown();
        }
      }
    });
  }

  private stopPolling(): void {
    if (this.pollingSubscription) {
      this.pollingSubscription.unsubscribe();
      this.pollingSubscription = null;
    }
  }

  private startCountdown(): void {
    if (!this.payment?.pixExpiration) return;

    this.updateCountdown();
    this.countdownInterval = setInterval(() => this.updateCountdown(), 1000);
  }

  private updateCountdown(): void {
    if (!this.payment?.pixExpiration) return;

    const expiration = new Date(this.payment.pixExpiration.replace(/(\d{2})\/(\d{2})\/(\d{4}) (\d{2}):(\d{2}):(\d{2})/, '$3-$2-$1T$4:$5:$6'));
    const now = new Date();
    const diff = expiration.getTime() - now.getTime();

    if (diff <= 0) {
      this.countdown = 'Expirado';
      this.stopCountdown();
      this.stopPolling();
      this.error = 'O tempo para pagamento expirou. Feche e tente novamente.';
      return;
    }

    const minutes = Math.floor(diff / 60000);
    const seconds = Math.floor((diff % 60000) / 1000);
    this.countdown = `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  }

  private stopCountdown(): void {
    if (this.countdownInterval) {
      clearInterval(this.countdownInterval);
      this.countdownInterval = null;
    }
  }

  onClose(): void {
    this.dialogRef.close(this.paymentConfirmed);
  }

  retry(): void {
    this.stopPolling();
    this.stopCountdown();
    this.createPayment();
  }
}
